import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { MAX_OVERRIDE_REASON_LENGTH } from "../lib/doubleBooking"

/**
 * The "book past the daily limit" panel (AB#606): what the server said, and the reason the override
 * needs. Shown only to somebody holding `appointments:manage`, and only once the server has answered
 * 409 `dailyLimitReached`. The caller owns the button that resubmits with the override. The reason is
 * the request's one `overrideReason`, shared with the double-booking override.
 */
export function DailyLimitOverrideSection({ id, message, reason, onReasonChange, disabled }: {
  /** Unique within the dialog, for the textarea's label. */
  id: string
  /** The server's refusal. */
  message: string
  reason: string
  onReasonChange: (reason: string) => void
  disabled?: boolean
}) {
  return (
    <section className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:bg-amber-950/30">
      <p className="text-sm font-medium">{message}</p>
      {/* Said before the click: the override is attributable, and Cal is told to skip its own checks
          for it, so it also books a host who is off that day. */}
      <p className="mt-1 text-xs text-muted-foreground">
        You can book this host past their daily limit anyway. The scheduling system's own checks are
        skipped for this booking, so make sure they are working at this time; the booking is recorded
        against your name with the reason below.
      </p>
      <Label htmlFor={id} className="mt-3 block text-xs font-medium">
        Reason for booking past the daily limit <span aria-hidden className="text-destructive">*</span>
      </Label>
      <Textarea
        id={id}
        className="mt-1.5"
        rows={2}
        maxLength={MAX_OVERRIDE_REASON_LENGTH}
        value={reason}
        disabled={disabled}
        placeholder="e.g. Walk-in with a shut-off notice; host agreed to stay"
        onChange={event => onReasonChange(event.target.value)}
      />
    </section>
  )
}
